import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Calendar, Clock, User, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import projectHoursService from '../services/projectHoursService';
import projectsService from '../services/projectsService';
import toast from 'react-hot-toast';

// Cores por cargo
const ROLE_COLORS = {
  GERENCIA: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
  RECEPCAO_COMPRAS_AGENDA: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  RECEPCAO_PROJETOS: { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-300' },
  PRODUCAO: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
  MONTAGEM_CLIENTE: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' }
};

const ROLE_LABELS = {
  GERENCIA: 'Gerência',
  RECEPCAO_COMPRAS_AGENDA: 'Compras/Agenda',
  RECEPCAO_PROJETOS: 'Recepção Projetos',
  PRODUCAO: 'Produção',
  MONTAGEM_CLIENTE: 'Montagem Cliente'
};

const ProjectHoursDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [hours, setHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedHours, setEditedHours] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadData();
  }, [projectId]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Carregar dados do projeto
      const projectResponse = await projectsService.getProjectById(projectId);
      setProject(projectResponse.data);

      // Carregar horas do projeto
      const hoursResponse = await projectHoursService.getProjectHours(projectId);
      const hoursData = hoursResponse.data?.data || hoursResponse.data || [];

      setHours(hoursData);

      // Inicializar editedHours com os valores atuais
      const initialEdits = {};
      hoursData.forEach(h => {
        initialEdits[h.id] = { ...h };
      });
      setEditedHours(initialEdits);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do projeto');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (hoursId, field, value) => {
    setEditedHours(prev => ({
      ...prev,
      [hoursId]: {
        ...prev[hoursId],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const hoursToSave = Object.values(editedHours).map(h => ({
        id: h.id,
        role: h.role,
        hours: h.estimated_hours || 0,
        scheduled_start_date: h.scheduled_start_date || null,
        scheduled_end_date: h.scheduled_end_date || null,
        scheduled_hours_per_day: h.scheduled_hours_per_day || null,
        assigned_to_user_id: h.assigned_to_user_id || null,
        notes: h.notes || null
      }));

      await projectHoursService.saveProjectHours(projectId, hoursToSave);

      toast.success('Horas salvas com sucesso!');
      setHasChanges(false);
      await loadData();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar horas');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { label: 'Pendente', class: 'bg-gray-100 text-gray-600' },
      SCHEDULED: { label: 'Agendado', class: 'bg-yellow-100 text-yellow-700' },
      IN_PROGRESS: { label: 'Em Andamento', class: 'bg-blue-100 text-blue-700' },
      COMPLETED: { label: 'Concluído', class: 'bg-green-100 text-green-700' }
    };
    const config = statusConfig[status] || statusConfig.PENDING;
    return (
      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${config.class}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3a7630]"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/projetos')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Planejamento de Horas
            </h1>
            <p className="text-gray-600">
              {project?.project_code} - {project?.project_name}
            </p>
          </div>
        </div>

        {hasChanges && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-[#3a7630] text-white px-4 py-2 rounded-lg hover:bg-[#2d5a25] disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        )}
      </div>

      {/* Info do Projeto */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <span className="text-sm text-gray-500">Cliente</span>
            <p className="font-medium">{project?.client_name}</p>
          </div>
          <div>
            <span className="text-sm text-gray-500">Status</span>
            <p className="font-medium">{project?.status}</p>
          </div>
          <div>
            <span className="text-sm text-gray-500">Orçamento</span>
            <p className="font-medium">
              {project?.estimated_budget
                ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(project.estimated_budget)
                : '-'}
            </p>
          </div>
          <div>
            <span className="text-sm text-gray-500">Criado em</span>
            <p className="font-medium">
              {project?.created_at
                ? new Date(project.created_at).toLocaleDateString('pt-BR')
                : '-'}
            </p>
          </div>
        </div>
      </div>

      {/* Lista de Horas por Cargo */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Horas por Cargo/Setor
          </h2>
          <p className="text-sm text-gray-500">
            Defina as horas estimadas e agende as datas de execução
          </p>
        </div>

        {hours.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <AlertCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>Nenhuma hora planejada para este projeto</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {hours.map((hour) => {
              const colors = ROLE_COLORS[hour.role] || ROLE_COLORS.PRODUCAO;
              const edit = editedHours[hour.id] || hour;

              return (
                <div key={hour.id} className={`p-4 ${colors.bg}`}>
                  {/* Header do Card */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 text-sm font-semibold rounded-full ${colors.bg} ${colors.text}`}>
                        {ROLE_LABELS[hour.role] || hour.role}
                      </span>
                      {getStatusBadge(edit.status || hour.status)}
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-gray-500">
                        Total: <span className="font-semibold text-gray-900">{edit.estimated_hours || hour.estimated_hours}h</span>
                      </span>
                    </div>
                  </div>

                  {/* Campos de Edição */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Horas Estimadas */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        <Clock className="w-3 h-3 inline mr-1" />
                        Horas Estimadas
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={edit.estimated_hours || ''}
                        onChange={(e) => handleFieldChange(hour.id, 'estimated_hours', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3a7630]"
                      />
                    </div>

                    {/* Horas por Dia */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        <Clock className="w-3 h-3 inline mr-1" />
                        Horas por Dia
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="24"
                        step="0.5"
                        value={edit.scheduled_hours_per_day || ''}
                        onChange={(e) => handleFieldChange(hour.id, 'scheduled_hours_per_day', parseFloat(e.target.value) || null)}
                        placeholder="Ex: 4"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3a7630]"
                      />
                    </div>

                    {/* Data Início */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        Data Início
                      </label>
                      <input
                        type="date"
                        value={edit.scheduled_start_date || ''}
                        onChange={(e) => handleFieldChange(hour.id, 'scheduled_start_date', e.target.value || null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3a7630]"
                      />
                    </div>

                    {/* Data Fim */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        Data Fim
                      </label>
                      <input
                        type="date"
                        value={edit.scheduled_end_date || ''}
                        onChange={(e) => handleFieldChange(hour.id, 'scheduled_end_date', e.target.value || null)}
                        min={edit.scheduled_start_date}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3a7630]"
                      />
                    </div>
                  </div>

                  {/* Observações */}
                  <div className="mt-4">
                    <label className="block text-xs text-gray-500 mb-1">
                      <FileText className="w-3 h-3 inline mr-1" />
                      Observações
                    </label>
                    <textarea
                      value={edit.notes || ''}
                      onChange={(e) => handleFieldChange(hour.id, 'notes', e.target.value)}
                      placeholder="Adicione observações..."
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3a7630]"
                    />
                  </div>

                  {/* Info de Status */}
                  <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                    {edit.scheduled_start_date && edit.scheduled_end_date ? (
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="w-3 h-3" />
                        Agendado de {new Date(edit.scheduled_start_date).toLocaleDateString('pt-BR')} até {new Date(edit.scheduled_end_date).toLocaleDateString('pt-BR')}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Sem agendamento - status: Pendente
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Botão Voltar */}
      <div className="mt-6">
        <button
          onClick={() => navigate('/projetos')}
          className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar para Projetos
        </button>
      </div>
    </div>
  );
};

export default ProjectHoursDetail;
